import type { Chart } from "../core/Chart";
import { isDrawingTool } from "../drawings/DrawingsController";
import { TOOLS } from "../drawings/types";

type Drag =
  | { mode: "pan"; startX: number; startOffset: number; startY: number; min: number; max: number; priceH: number }
  | { mode: "scaleX"; lastX: number }
  | { mode: "scaleY"; lastY: number; min: number; max: number };

const DBL_MS = 350, DBL_PX = 12, QUICK_DRAG_PX = 4;

// Pointer Events (mouse + touch + pen): crosshair, free 2-D pan (drag), zoom (wheel, ctrl+wheel / two-finger pinch, time-axis drag)
// anchored at the cursor, Y-axis scale drag, double-click/double-tap = Quick Price Range Measurement on the plot (reset view on the axes).
// Drawing placement/selection/drag is delegated to the DrawingsController.
export function attachInteractions(chart: Chart): () => void {
  const canvas = chart.canvas;
  let drag: Drag | null = null;
  let pinch: { dist: number } | null = null;
  const pointers = new Map<number, { x: number; y: number }>();
  let lastTap: { t: number; x: number; y: number } | null = null;
  let quickStart: { x: number; y: number } | null = null;

  const xy = (e: { clientX: number; clientY: number }) => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const N = () => chart.candles.length || 200;

  const onMove = (e: PointerEvent) => {
    const { x, y } = xy(e);
    const f = chart.currentFrame;
    if (pointers.has(e.pointerId)) pointers.set(e.pointerId, { x, y });
    if (pinch && f && pointers.size >= 2) {
      const [p1, p2] = [...pointers.values()];
      const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1, cx = (p1.x + p2.x) / 2;
      chart.viewport.zoomAt(pinch.dist / dist, N(), f.fracIndexAt(cx), (cx - f.L.plotLeft) / f.L.plotW);
      pinch.dist = dist;
      chart.requestRender();
      return;
    }
    chart.crosshair = { x, y, active: true };
    if (drag && f) {
      if (drag.mode === "pan") {
        chart.viewport.setOffset(drag.startOffset + (x - drag.startX) / f.candleW, N());
        const dy = y - drag.startY;
        if (dy !== 0 || chart.priceScale.override) {
          const dp = (dy / drag.priceH) * (drag.max - drag.min); // free vertical pan: drag down → see higher prices
          chart.priceScale.override = { min: drag.min + dp, max: drag.max + dp };
        }
      } else if (drag.mode === "scaleX") {
        chart.viewport.zoomAt(1 - (x - drag.lastX) / 250, N(), f.endFrac, 1);
        drag.lastX = x;
      } else {
        const factor = 1 + (y - drag.lastY) / 200;
        chart.priceScale.scaleBy(factor, { min: drag.min, max: drag.max });
        drag.lastY = y;
        drag.min = chart.priceScale.override!.min;
        drag.max = chart.priceScale.override!.max;
      }
    }
    if (f && x < f.L.plotRight) {
      const c = chart.candles[f.indexAt(x)];
      chart.emitHover(c ?? null);
      chart.drawings.onMove(x, y, f, chart.opts.activeTool, (e.buttons & 1) === 1);
      canvas.style.cursor = drag ? (drag.mode === "pan" ? "grabbing" : drag.mode === "scaleX" ? "ew-resize" : "ns-resize")
        : chart.drawings.dragging ? "grabbing"
        : chart.drawings.quick ? "crosshair"
        : chart.opts.activeTool === "select" && chart.drawings.hitAt(x, y, f) ? "pointer"
        : chart.opts.activeTool === "eraser" ? "not-allowed" : "";
    } else if (f) {
      canvas.style.cursor = y > f.L.h - f.L.bottomAxis ? "ew-resize" : "ns-resize";
    }
    chart.requestRender();
  };

  const onLeave = () => {
    if (pointers.size) return; // pointer captured while dragging — keep state
    chart.crosshair.active = false;
    chart.drawings.snap = null;
    chart.emitHover(null);
    chart.requestRender();
  };

  // Double-click / double-tap: axes → reset view · plot → finish variable tool / place drawing point / edit text / Quick Measure origin.
  const onDouble = (x: number, y: number, f: NonNullable<Chart["currentFrame"]>) => {
    const tool = chart.opts.activeTool;
    if (y > f.L.h - f.L.bottomAxis || x > f.L.plotRight) { chart.resetView(); return; }
    const p = chart.drawings.pending;
    if (p && chart.drawings.quick) { chart.drawings.onDown(x, y, f, tool); chart.requestRender(); return; }
    if (p && TOOLS[p.type].variable) { chart.finishPending(); return; }
    if (isDrawingTool(tool)) { chart.drawings.onDown(x, y, f, tool); chart.requestRender(); return; }
    const id = chart.drawings.hitAt(x, y, f);
    const obj = id ? chart.opts.drawings.find((d) => d.id === id) : null;
    if (obj && obj.text != null) { chart.editText(obj.id); return; }
    chart.drawings.startQuickMeasure(f, x, y);
    quickStart = { x, y };
    chart.requestRender();
  };

  const onDown = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const { x, y } = xy(e);
    pointers.set(e.pointerId, { x, y });
    try { canvas.setPointerCapture(e.pointerId); } catch { /* not supported */ }
    const f = chart.currentFrame;
    if (!f) return;
    if (pointers.size >= 2) { // second finger → pinch zoom, cancel any pan / quick origin
      const [p1, p2] = [...pointers.values()];
      pinch = { dist: Math.hypot(p2.x - p1.x, p2.y - p1.y) || 1 };
      drag = null; quickStart = null; lastTap = null;
      if (chart.drawings.quick) chart.cancelDrawing();
      return;
    }
    const now = performance.now();
    const dbl = !!lastTap && now - lastTap.t < DBL_MS && Math.hypot(x - lastTap.x, y - lastTap.y) < DBL_PX;
    lastTap = dbl ? null : { t: now, x, y };
    if (dbl) { drag = null; onDouble(x, y, f); return; }
    if (y > f.L.h - f.L.bottomAxis) { drag = { mode: "scaleX", lastX: x }; return; }
    if (x > f.L.plotRight) { drag = { mode: "scaleY", lastY: y, min: f.minP, max: f.maxP }; return; }
    const wasQuick = chart.drawings.quick;
    if (chart.drawings.onDown(x, y, f, chart.opts.activeTool)) { if (wasQuick) lastTap = null; chart.requestRender(); return; }
    drag = { mode: "pan", startX: x, startOffset: chart.viewport.offset, startY: y, min: f.minP, max: f.maxP, priceH: f.L.priceH };
  };

  const onUp = (e: PointerEvent) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinch = null;
    if (pointers.size) return;
    drag = null;
    if (quickStart && chart.drawings.quick) { // dbl-click + drag + release = finished measurement; no drag = wait for the next click
      const { x, y } = xy(e);
      const f = chart.currentFrame;
      if (f && Math.hypot(x - quickStart.x, y - quickStart.y) > QUICK_DRAG_PX) { chart.drawings.onDown(x, y, f, chart.opts.activeTool); lastTap = null; }
    }
    quickStart = null;
    chart.drawings.onUp();
    chart.requestRender();
  };

  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const f = chart.currentFrame;
    if (!f) return;
    const horizontal = Math.abs(e.deltaX) > Math.abs(e.deltaY);
    if (!e.ctrlKey && !e.metaKey && (horizontal || e.shiftKey)) {
      const px = horizontal ? e.deltaX : e.deltaY;
      chart.viewport.panBy(-px / f.candleW, N());
    } else {
      const factor = e.ctrlKey || e.metaKey ? Math.exp(e.deltaY * 0.01) : e.deltaY > 0 ? 1.15 : 0.87;
      const { x } = xy(e);
      const inPlot = x >= f.L.plotLeft && x <= f.L.plotRight;
      chart.viewport.zoomAt(factor, N(), inPlot ? f.fracIndexAt(x) : f.endFrac, inPlot ? (x - f.L.plotLeft) / f.L.plotW : 1);
    }
    chart.requestRender();
  };

  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerleave", onLeave);
  canvas.addEventListener("pointerdown", onDown);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onUp);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  return () => {
    canvas.removeEventListener("pointermove", onMove);
    canvas.removeEventListener("pointerleave", onLeave);
    canvas.removeEventListener("pointerdown", onDown);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);
    canvas.removeEventListener("wheel", onWheel);
  };
}
